import type {
    IExtractionTriggerResponse,
    IExtractionTriggerConflictResponse,
    IExtractionUnavailableResponse,
} from "@/api/documents";

const PIPELINE_LABELS: Record<string, string> = {
    DOCUMENT_FIELD_EXTRACTION: "fields",
    DOCUMENT_FORM_EXTRACTION: "certificate of insurance",
};

const PIPELINE_NOUN_LABELS: Record<string, string> = {
    DOCUMENT_FIELD_EXTRACTION: "Field extraction",
    DOCUMENT_FORM_EXTRACTION: "Certificate extraction",
};

const REASON_LABELS: Record<string, string> = {
    already_running: "already in progress",
    already_succeeded: "already complete",
    not_applicable: "not available for this document",
};

function pipelineLabel(pipeline: string): string {
    return (
        PIPELINE_LABELS[pipeline] ?? pipeline.toLowerCase().replace(/_/g, " ")
    );
}

function pipelineNounLabel(pipeline: string): string {
    return PIPELINE_NOUN_LABELS[pipeline] ?? pipelineLabel(pipeline);
}

export function formatExtractionToast(
    response: IExtractionTriggerResponse
): string {
    if (response.enqueued.length > 0) {
        const enqueued = response.enqueued.map(pipelineLabel).join(" and ");
        const parts = [`Started extracting ${enqueued}`];

        for (const skip of response.skipped) {
            const label = pipelineLabel(skip.pipeline);
            const reason = REASON_LABELS[skip.reason] ?? skip.reason;
            parts.push(`${label} was ${reason}`);
        }

        return parts.join("; ");
    }

    if (response.skipped.length > 0) {
        const skip = response.skipped[0];
        const label = pipelineNounLabel(skip.pipeline);
        const reason = REASON_LABELS[skip.reason] ?? skip.reason;
        return `${label} is ${reason}`;
    }

    return "No extraction pipelines available";
}

export function formatExtractionConflictToast(
    data: IExtractionTriggerConflictResponse
): string {
    const items = data.detail.running_or_queued;
    const labels = items.map((item) => pipelineLabel(item.pipeline));

    if (
        labels.length === 1 &&
        items[0].pipeline === "DOCUMENT_FIELD_EXTRACTION"
    ) {
        return "Field extraction is already in progress";
    }

    return `Extraction is already in progress for ${labels.join(" and ")}`;
}

export function formatExtractionUnavailableToast(
    data: IExtractionUnavailableResponse
): string {
    const { field_extraction_available, form_extraction_available } =
        data.availability;
    if (!field_extraction_available && !form_extraction_available) {
        return "This document has neither declarations nor a certificate of insurance available for extraction.";
    }
    if (!field_extraction_available) {
        return "Field extraction is not available for this document.";
    }
    if (!form_extraction_available) {
        return "Certificate of insurance extraction is not available for this document.";
    }
    return data.detail;
}
